import { v4 as uuidv4 } from 'uuid';
import type {
    IUser,
    IAnalyticsMgr,
    IDataSecurityMgr,
    ICoreFlagMgr,
    IUserRelevantAppDetails,
    IRUMMgr,
    IInteractionMgr,
    InternalUserConfig
} from '@nab/x-types';
import { UserBuilder, defaultHashUserId, IUserBuilder } from '@nab/nab-x-sdk-browser';
// eslint-disable-next-line import/no-named-default

let shellUser: IUser;

let defaultUserConfig: Partial<InternalUserConfig> = {
    id: uuidv4(),
    hashUserId: defaultHashUserId,
    authenticated: false
};

export const buildUser = (
    userConfig: Partial<InternalUserConfig>,
    app: IUserRelevantAppDetails,
    analyticsMgr: IAnalyticsMgr,
    dataSecurityMgr: IDataSecurityMgr,
    flagMgr: ICoreFlagMgr,
    rumMgr: IRUMMgr,
    interactionMgr: IInteractionMgr
): IUser => {
    const config: Partial<InternalUserConfig> = {
        ...defaultUserConfig,
        ...userConfig
    };
    const userBuilder: IUserBuilder<IUser> = new UserBuilder();
    shellUser = userBuilder
        .id(config.id, config.hashUserId ? config.hashUserId(config.id) : '')
        .app(app)
        .authenticated(config.authenticated)
        .sessionId(config.sessionId)
        .details(config.details)
        .context(config.context)
        .userOrg(config.org)
        .userType(config.type)
        .actAs(config.as)
        .analyticsManager(analyticsMgr)
        .dataSecurityManager(dataSecurityMgr)
        .flagMgr(flagMgr)
        .rumManager(rumMgr)
        .interactionManager(interactionMgr)
        .userBrand(config.brand)
        .build();

    return shellUser;
};

export const getUserForShell = (): IUser => shellUser;

/**
 * Creates a User service which is pre-configured for a specific app.
 * - Ensures methods can be pre-configured so mini-apps don't need to supply their own appId or instanceId as method params
 * - Ensures shared service is encapsulated so a rogue mini-app can't mutate or break it for all other apps
 * - Enables support for sessionId/instanceId correlation for analytics or any future services
 *
 * The pattern here is similar to that of action functions by where we create a tailored set of functions for each app
 * pre-configured to work with the app's appId and instanceId
 */
export function getUserForApp(appDetails: IUserRelevantAppDetails): IUser {
    return shellUser.forkForApp(appDetails);
}

/**
 * Get the singleton sessionId
 */
export function getSessionId(): string {
    return defaultUserConfig.sessionId;
}

/**
 * Set user config into the central place - the singleton defaultUserConfig
 * @param userConfig user config to be set into the central place
 * @returns Promise<InternalUserConfig>
 */
export async function setUserConfig(userConfig: Partial<InternalUserConfig>): Promise<Partial<InternalUserConfig>> {
    const { authenticated, sessionId } = defaultUserConfig;
    const newUserConfig = { ...userConfig };
    // if user has just logged out, reset user ID
    if (typeof userConfig.authenticated !== 'undefined' && !userConfig.authenticated && !!authenticated) {
        newUserConfig.id = userConfig.id || uuidv4();
        newUserConfig.sessionId = userConfig.sessionId || uuidv4();
    } else {
        newUserConfig.sessionId = userConfig.sessionId || sessionId || uuidv4();
    }

    defaultUserConfig = { ...defaultUserConfig, ...newUserConfig };
    return defaultUserConfig;
}

export function getUserConfig(): Partial<InternalUserConfig> {
    return defaultUserConfig;
}

export const resetUserRegistry = () => {
    shellUser = undefined;
};
